/**
 * src/api/printing/types.ts — Schemas Zod + types domaine pour le module PrintingApi.
 *
 * Invariants :
 *   4.B.4  — 0 any : tous les types sont stricts ou Zod-inférés
 *   4.A.16 — Mentions NF525 obligatoires sur ticket : vérifiées par PrintService legacy (wrap)
 *   4.A.15 — Aucun PII (client_name, email) dans PrintResult loggé
 */

import { z } from 'zod';

// ---------------------------------------------------------------------------
// PrinterDevice
// ---------------------------------------------------------------------------

export const printerConnectionTypeSchema = z.enum(['sunmi', 'bluetooth', 'network', 'usb']);
export type PrinterConnectionType = z.infer<typeof printerConnectionTypeSchema>;

export const printerTypeSchema = z.enum(['receipt', 'kitchen', 'bar', 'label']);
export type PrinterType = z.infer<typeof printerTypeSchema>;

/**
 * Représente un périphérique d'impression connu ou découvert.
 * Correspond à PrinterConfig legacy + DiscoveredPrinter.
 */
export const printerDeviceSchema = z.object({
  id: z.string(),
  name: z.string(),
  connectionType: printerConnectionTypeSchema,
  printerType: printerTypeSchema,
  btMacAddress: z.string().nullable(),
  ipAddress: z.string().nullable(),
  port: z.number().int().default(9100),
  registerId: z.string().nullable(),
  isDefault: z.boolean().default(false),
  paperWidth: z.number().int().default(80),
  charPerLine: z.number().int().default(48),
});

export type PrinterDevice = z.infer<typeof printerDeviceSchema>;

// ---------------------------------------------------------------------------
// PrintOptions
// ---------------------------------------------------------------------------

/**
 * Options communes à toutes les opérations d'impression.
 * tenant_id JAMAIS ici — déduit du JWT via RLS (invariant 4.A.10).
 */
export const printOptionsSchema = z.object({
  /** Identifiant de la caisse (pour lookup de l'imprimante assignée). */
  registerId: z.string().uuid().optional(),
  /** Forcer un périphérique spécifique (sinon auto-detect). */
  deviceId: z.string().optional(),
  /** Marquer comme duplicata (bandeau *** DUPLICATA *** NF525 art. 286). */
  isDuplicate: z.boolean().default(false),
});

export type PrintOptions = z.infer<typeof printOptionsSchema>;

// ---------------------------------------------------------------------------
// TicketPayload — payload structuré pour impression libre (non fiscale)
// ---------------------------------------------------------------------------

/**
 * Payload pour printReceipt() — impression libre, non fiscale.
 * Pour les impressions fiscales (ticket de caisse, Z), utiliser printTicket() / printZ().
 */
export const receiptPayloadSchema = z.object({
  /** Nom du commerce. */
  storeName: z.string().min(1),
  /** Lignes de texte libre (format ESC/POS géré en interne). */
  lines: z.array(z.string()),
  /** Date ISO8601 ou string lisible. */
  date: z.string(),
  /** Options d'impression. */
  options: printOptionsSchema.optional(),
});

export type ReceiptPayload = z.infer<typeof receiptPayloadSchema>;

// ---------------------------------------------------------------------------
// KitchenTicketPayload — bon de commande cuisine (KOT) / bar
// ---------------------------------------------------------------------------

export const kitchenTicketDestinationSchema = z.enum(['CUISINE', 'BAR']);
export type KitchenTicketDestination = z.infer<typeof kitchenTicketDestinationSchema>;

export const kitchenTicketItemSchema = z.object({
  name: z.string().min(1),
  quantity: z.number().int().positive(),
  notes: z.string().optional(),
  /**
   * Optionnel : permet le split du KOT par fonction d'imprimante (lot 4).
   * nullable : order_items.product_id est nullable en base (prix libre) —
   * un null ne doit jamais invalider le KOT entier, il route vers le
   * groupe par défaut.
   */
  productId: z.string().uuid().nullable().optional(),
});
export type KitchenTicketItem = z.infer<typeof kitchenTicketItemSchema>;

/**
 * Payload pour PrintingApi.printKitchenTicket().
 * Pas de PII (invariant 4.A.15) : server_name est un alias staff (pas un email).
 */
export const kitchenTicketPayloadSchema = z.object({
  destination: kitchenTicketDestinationSchema,
  tableNumber: z.string().optional(),
  orderNumber: z.string().min(1),
  serverName: z.string().min(1),
  items: z.array(kitchenTicketItemSchema).min(1),
  notes: z.string().optional(),
  /** Service de l'envoi (Entrée/Plat/Dessert/Boisson) — affiché en tête du bon. */
  course: z.string().optional(),
  /** Bon de réclame (rappel d'un service déjà envoyé) — bandeau RÉCLAME en tête. */
  reclame: z.boolean().optional(),
});

export type KitchenTicketPayload = z.infer<typeof kitchenTicketPayloadSchema>;

// ---------------------------------------------------------------------------
// PrintResult
// ---------------------------------------------------------------------------

export const printRouteSchema = z.enum([
  'sunmi_aidl',
  'sunmi_cloud',
  'bluetooth',
  'network',
  'edge_function',
  'fallback',
]);
export type PrintRoute = z.infer<typeof printRouteSchema>;

/**
 * Résultat d'une opération d'impression réussie.
 * Invariant 4.A.15 : aucun PII dans ce résultat (pas de client_name, email, IBAN).
 */
export const printResultSchema = z.object({
  /** Route physique empruntée. */
  route: printRouteSchema,
  /** Nom de l'imprimante utilisée. */
  deviceName: z.string().nullable(),
  /** Durée de l'opération en ms (pour logging). */
  durationMs: z.number().int().nonnegative(),
  /** Identifiant de la tentative de log (uuid retourné par log_print_attempt). */
  logId: z.string().nullable(),
  /** True si issu d'un duplicata. */
  isDuplicate: z.boolean().default(false),
});

export type PrintResult = z.infer<typeof printResultSchema>;
